// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canSubmitRequisition,
    getCurrentCartState,
    hasQuoteRequiredProducts,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@rpsc/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@rpsc/App/Components/MiniCart/MiniCart.helpers";
import MiniCartLineCard from "@rpsc/App/Components/MiniCart/MiniCartLineCard";
import Notify from "@rpsc/App/Components/Notify/Notify";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { xHasProductsWithInvalidPrice } from "@rpsc/App/Store/Selectors/CartSelectors";
import { xIsAuthenticated, xIsCheckoutDisabled } from "@rpsc/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@rpsc/App/Store/Selectors/SettingsSelectors";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    isPopupShown?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const cart = getCurrentCartState(state).value;
    const isCheckoutDisabled = xIsCheckoutDisabled(cart);
    const currentPage = getCurrentPage(state);
    const {
        cartSettings: { onePageCheckout },
    } = getSettingsCollection(state);
    return {
        isCheckoutDisabled,
        isPunchOutOrder: isPunchOutOrder(cart),
        shouldLoadCart:
            !getCurrentCartState(state).value &&
            !getCurrentCartState(state).isLoading &&
            currentPage.type !== "CartPage",
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay || 0,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        loginPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        requestQuotePageUrl: getPageLinkByPageType(state, "RequestQuotePage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        cart,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
        isLoading: state.data.carts.isLoading,
        isAuthenticated: xIsAuthenticated(state),
        hasInvalidPriceItems: xHasProductsWithInvalidPrice(cart),
        headerHeight: (state as CustomAppState).xHeader.headerHeight,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        rememberMe: getSession(state).rememberMe,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        canRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface MiniCartStyles {
    wrapper?: InjectableCss;
    centerWrapper?: InjectableCss;
    notifyWrapper?: InjectableCss;
    noCartLinesNotify?: TypographyProps;
    totalWrapper?: InjectableCss;
    title?: TypographyProps;
    totalItems?: TypographyProps;
    cartLinesContainer?: InjectableCss;
    cartLinesWrapper?: InjectableCss;
    bottomWrapper?: InjectableCss;
    bottomSectionWrapper?: InjectableCss;
    totalItem?: InjectableCss;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    checkoutButton?: ButtonPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    cantCheckoutCart?: InjectableCss;
    notify?: TypographyProps;
    fancyScroll?: InjectableCss;
}

export const miniCartStyles: MiniCartStyles = {};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    cart,
    cartPageUrl,
    requestQuotePageUrl,
    isPunchOutOrder,
    isCheckoutDisabled,
    totalCountDisplay,
    orderSubTotalDisplay,
    removeCartLine,
    updateCartLine,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
    isUpdatingCartLine,
    isRemovingCartLine,
    isLoading,
    checkoutShippingPageUrl,
    isPopupShown,
    hasInvalidPriceItems,
    headerHeight,
    onePageCheckout,
    onePageCheckoutPageUrl,
    checkoutReviewAndSubmitPageUrl,
    rememberMe,
    signInUrl,
    canRequisition,
    requisitionConfirmationPageUrl,
    hasQuoteRequiredProducts,
}) => {
    const history = useHistory();
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const isQuoteRequired = cart?.cartLines?.some(cartline => cartline.quoteRequired) || hasInvalidPriceItems;
    const productsCannotBePurchased = cart?.cartLines?.some(cartLine => {
        return cartLine.isRestricted || !cartLine.isActive || cartLine.cantBuy;
    });

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const checkoutHandler = () => {
        if (!cart) {
            return;
        }
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);
        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        if (onePageCheckout && onePageCheckoutPageUrl) {
            hideTippy();
            if (!isAuthenticated && !rememberMe) {
                history.push(`${signInUrl}?returnUrl=${encodeURIComponent(onePageCheckoutPageUrl)}`);
            } else {
                history.push(
                    isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl,
                );
            }
            return;
        }

        setIsCheckoutLoading(true);

        if (canBypassCheckoutAddress) {
            setIsCheckoutLoading(false);
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }
        preloadCheckoutShippingData({
            onSuccess: () => {
                setIsCheckoutLoading(false);
                history.push(checkoutShippingPageUrl!);
            },
        });
    };

    // const requestQuoteHandler = () => {
    //     TODO: Go to request quote once its implemented
    //     hideTippy();
    //     history.push(requestQuotePageUrl || "/RequestQuote");
    // };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const handleViewCarClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        hideTippy();
        history.push(cartPageUrl);
    };

    if (!cart && isLoading) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!cart) {
        return null;
    }

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: any) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const cartLines = cart.cartLines ?? [];
    const isLoadingCart = isLoading[cart.id];
    const isCheckoutButtonDisabled = isCheckoutDisabled || isCheckoutLoading || isLoadingCart;

    const BottomSection = (
        <StyledWrapper {...styles.bottomSectionWrapper}>
            <Button {...styles.viewCartButton} onClick={handleViewCarClick} aria-label="View Cart">
                {translate("View Cart")}
            </Button>
            {canRequisition ? (
                <Button
                    {...styles.checkoutButton}
                    onClick={submitRequisitionClickHandler}
                    data-test-selector="cartTotal_submitRequisition"
                    aria-label="Submit Requisition"
                >
                    {translate("Submit Requisition")}
                </Button>
            ) : (
                <>
                    <Button
                        {...styles.checkoutButton}
                        onClick={checkoutHandler}
                        disabled={isCheckoutButtonDisabled}
                        aria-label={cart?.requiresApproval ? "Checkout for Approval" : "Continue to Checkout"}
                    >
                        {cart?.requiresApproval
                            ? translate("Checkout for Approval")
                            : translate("Continue to Checkout")}
                    </Button>

                    <TwoButtonModal
                        headlineText=""
                        modalIsOpen={quoteRequiredModalIsOpen}
                        messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Continue")}
                        onCancel={() => setQuoteRequiredModalIsOpen(false)}
                        onSubmit={checkoutHandler}
                        submitTestSelector="QuoteInCartMessageSubmit"
                        dataTestSelector="twoButtonModal_RequiresQuote"
                    ></TwoButtonModal>
                </>
            )}
        </StyledWrapper>
    );

    if (!cartLines?.length) {
        return (
            <StyledWrapper {...styles.wrapper} {...{ headerHeight }}>
                <StyledWrapper {...styles.notifyWrapper}>
                    <Notify {...styles.noCartLinesNotify} type="info" showClose={false}>
                        {siteMessage("Custom_MiniCart_EmptyMessage")}
                    </Notify>
                </StyledWrapper>
                <StyledWrapper {...styles.bottomWrapper}>
                    <StyledWrapper {...styles.bottomSectionWrapper}>
                        <StyledWrapper {...styles.totalItem}>
                            <Typography {...styles.totalLabel}>{translate("Items: ")}</Typography>
                            <Typography {...styles.totalValue}>{totalCountDisplay}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.totalItem}>
                            <Typography {...styles.totalLabel}>{`${translate("Subtotal")}:`}</Typography>
                            <Typography {...styles.totalValue}>{orderSubTotalDisplay}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    {BottomSection}
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={isLoadingCart || isUpdatingCartLine || isRemovingCartLine}>
            <StyledWrapper {...styles.wrapper} {...{ headerHeight }}>
                <FancyScroll extendedStyles={styles.fancyScroll} refElement={parentBoxRef} ref={fancyScrollRef}>
                    <StyledWrapper
                        {...styles.cartLinesWrapper}
                        ref={parentBoxRef}
                        data-test-selector="cartLines-container"
                        {...{ headerHeight }}
                    >
                        {isQuoteRequired && (
                            <Notify {...styles.notify} type="warning" data-test-selector="cartline_errorMessage">
                                {siteMessage("Custom_Cart_Quote_Required", "Some items in your cart require a quote.")}
                            </Notify>
                        )}
                        {productsCannotBePurchased && (
                            <StyledWrapper {...styles.cantCheckoutCart}>
                                <Notify
                                    {...styles.notify}
                                    type="warning"
                                    data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                                    showClose={false}
                                >
                                    {siteMessage("Cart_ProductsCannotBePurchased")}
                                </Notify>
                            </StyledWrapper>
                        )}

                        <StyledWrapper {...styles.cartLinesContainer} {...{ headerHeight }}>
                            {cartLines?.map(cartLine => {
                                const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                                return (
                                    <MiniCartLineCard
                                        key={`miniCartLine_${cartLine.id}`}
                                        cartLine={cartLine}
                                        cart={cart}
                                        hideTippy={hideTippy}
                                        qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                        removeCartLineClickHandler={removeCartLineClickHandler}
                                        errorMessages={errorMessages}
                                    />
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                </FancyScroll>
                <StyledWrapper {...styles.bottomWrapper}>
                    <StyledWrapper {...styles.bottomSectionWrapper}>
                        <StyledWrapper {...styles.totalItem}>
                            <Typography {...styles.totalLabel}>{translate("Items: ")}</Typography>
                            <Typography {...styles.totalValue}>{totalCountDisplay}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.totalItem}>
                            <Typography {...styles.totalLabel}>{`${translate("Subtotal")}:`}</Typography>
                            <Typography {...styles.totalValue}>{orderSubTotalDisplay}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    {BottomSection}
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
